class PointerParticle {
    constructor(spread, speed, component) {
        const { ctx, pointer, hue } = component;

        this.ctx = ctx;
        this.x = pointer.x;
        this.y = pointer.y;
        this.mx = pointer.mx * 0.1;
        this.my = pointer.my * 0.1;
        this.size = Math.random() + 1;
        this.decay = 0.05;
        this.speed = speed * 0.08;
        this.spread = spread * this.speed;
        this.spreadX = (Math.random() - 0.5) * this.spread - this.mx;
        this.spreadY = (Math.random() - 0.5) * this.spread - this.my;
        this.color = `hsl(${hue}deg 90% 60%)`;
    }

    draw() {
        this.ctx.fillStyle = this.color;
        this.ctx.beginPath();
        this.ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        this.ctx.fill();
    }

    collapse() {
        this.size -= this.decay;
    }

    trail() {
        this.x += this.spreadX * this.size;
        this.y += this.spreadY * this.size;
    }

    update() {
        this.draw();
        this.trail();
        this.collapse();
    }
}

class PointerParticles extends HTMLElement {
    static register(tag = "pointer-particles") {
        if ("customElements" in window && !customElements.get(tag)) {
            customElements.define(tag, this);
        }
    }

    static css = `
    :host {
        display: block;
        position: fixed;
        inset: 0;
        pointer-events: none;
        z-index: 9999;
        background: transparent;
    }
    canvas {
        position: fixed;
        inset: 0;
        width: 100%;
        height: 100%;
        display: block;
        pointer-events: none;
    }
  `;

    constructor() {
        super();
        this.canvas = null;
        this.ctx = null;
        this.fps = 60;
        this.msPerFrame = 1000 / this.fps;
        this.timePrevious = 0;
        this.particles = [];
        this.pointer = { x: 0, y: 0, mx: 0, my: 0 };
        this.hue = 0;

        this.pendingPointer = null;
        this.pixelRatio = window.devicePixelRatio || 1;
        this.lastPointer = { x: 0, y: 0 };

        // RAF idle management
        this.rafId = null;
        this.idleTimer = null;
        this.isAnimating = false;
    }

    connectedCallback() {
        const canvas = document.createElement("canvas");
        const sheet = new CSSStyleSheet();

        this.shadowroot = this.attachShadow({ mode: "open" });

        sheet.replaceSync(PointerParticles.css);
        this.shadowroot.adoptedStyleSheets = [sheet];

        this.shadowroot.append(canvas);

        this.canvas = this.shadowroot.querySelector("canvas");
        this.ctx = this.canvas.getContext("2d");

        Object.assign(this.canvas.style, {
            position: "fixed",
            inset: "0",
            width: "100%",
            height: "100%",
            display: "block",
            pointerEvents: "none",
        });

        this.setCanvasDimensions();
        this.setupEvents();

        this.timePrevious = performance.now();
        // RAF starts only on first pointer interaction
    }

    setCanvasDimensions() {
        const w = Math.max(1, window.innerWidth);
        const h = Math.max(1, window.innerHeight);
        const pr = this.pixelRatio;

        this.canvas.width = Math.floor(w * pr);
        this.canvas.height = Math.floor(h * pr);

        this.canvas.style.width = w + "px";
        this.canvas.style.height = h + "px";

        this.ctx.setTransform(pr, 0, 0, pr, 0, 0);
    }

    setupEvents() {
        window.addEventListener("pointermove", (event) => {
            this.pendingPointer = event;
            this.startAnimation();
            this.resetIdleTimer();
        }, { passive: true });

        window.addEventListener("click", (event) => {
            this.createParticles(event, {
                count: 40,
                speed: Math.random() + 1,
                spread: Math.random() * 60 + 20,
            });
            this.startAnimation();
            this.resetIdleTimer();
        });

        window.addEventListener("resize", () => this.setCanvasDimensions());

        document.addEventListener("visibilitychange", () => {
            if (document.hidden) {
                this.stopAnimation();
            } else if (this.particles.length > 0) {
                this.startAnimation();
            }
        });
    }

    startAnimation() {
        if (this.isAnimating) return;
        this.isAnimating = true;
        this.timePrevious = performance.now();
        this.rafId = requestAnimationFrame(() => this.animateParticles());
    }

    stopAnimation() {
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        this.isAnimating = false;
    }

    resetIdleTimer() {
        clearTimeout(this.idleTimer);
        this.idleTimer = setTimeout(() => {
            if (this.particles.length === 0) {
                this.stopAnimation();
            }
        }, 2000);
    }

    createParticles(event, { count = 5, speed = 1, spread = 1 } = {}) {
        this.setPointerValues(event);

        for (let i = 0; i < count; i++) {
            this.particles.push(new PointerParticle(spread, speed, this));
        }
    }

    setPointerValues(event) {
        const rect = this.canvas.getBoundingClientRect();
        const cx = event.clientX - rect.left;
        const cy = event.clientY - rect.top;

        this.pointer.x = cx;
        this.pointer.y = cy;

        this.pointer.mx = typeof event.movementX === "number" ? event.movementX : (cx - (this.lastPointer.x || cx));
        this.pointer.my = typeof event.movementY === "number" ? event.movementY : (cy - (this.lastPointer.y || cy));

        this.lastPointer.x = cx;
        this.lastPointer.y = cy;
    }

    getPointerVelocity(event) {
        const a = event.movementX || 0;
        const b = event.movementY || 0;
        const c = Math.floor(Math.sqrt(a * a + b * b));
        return c || 1;
    }

    handleParticles() {
        for (let i = 0; i < this.particles.length; i++) {
            this.particles[i].update();

            if (this.particles[i].size <= 0.1) {
                this.particles.splice(i, 1);
                i--;
            }
        }

        if (this.particles.length === 0 && !this.pendingPointer) {
            this.stopAnimation();
        }
    }

    animateParticles() {
        if (!this.isAnimating) return;

        this.rafId = requestAnimationFrame(() => this.animateParticles());

        const timeNow = performance.now();
        const timePassed = timeNow - this.timePrevious;

        if (timePassed < this.msPerFrame) return;

        const excessTime = timePassed % this.msPerFrame;
        this.timePrevious = timeNow - excessTime;

        const w = this.canvas.width / this.pixelRatio;
        const h = this.canvas.height / this.pixelRatio;
        this.ctx.clearRect(0, 0, w, h);

        this.hue = (this.hue + 3) % 360;

        if (this.pendingPointer) {
            const vel = this.getPointerVelocity(this.pendingPointer);
            const count = Math.max(1, Math.min(8, Math.floor(vel / 1) || 3));

            this.createParticles(this.pendingPointer, {
                count,
                speed: vel,
                spread: 1 + vel * 0.00005,
            });

            this.pendingPointer = null;
        }

        this.handleParticles();
    }
}

PointerParticles.register();
